import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { howtoApi, uploadApi } from '../../api/client';
import type { HowToCategory, HowToGalleryItem, HowToGuideStatus } from '../../api/modules/howto';
import { useAuth } from '../../hooks/useAuth';
import { generateSlugFromTitle } from './utils';

export interface HowToEditorForm {
  title: string;
  slug: string;
  categoryId: string;
  status: HowToGuideStatus;
  sortOrder: number;
  contentHtml: string;
  videoUrl: string;
  galleryJson: HowToGalleryItem[];
  slugTouched: boolean;
}

const emptyForm = (): HowToEditorForm => ({
  title: '',
  slug: '',
  categoryId: '',
  status: 'draft',
  sortOrder: 0,
  contentHtml: '',
  videoUrl: '',
  galleryJson: [],
  slugTouched: false,
});

export function useHowToEditor() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const { isAuthenticated, isAdmin, loading: authLoading, logout } = useAuth();

  const [form, setForm] = useState<HowToEditorForm>(emptyForm());
  const [categories, setCategories] = useState<HowToCategory[]>([]);
  const [isLoading, setIsLoading] = useState(!isNew);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [guideId, setGuideId] = useState<string | null>(isNew ? null : id);

  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !isAdmin)) {
      navigate('/dashboard');
    }
  }, [authLoading, isAuthenticated, isAdmin, navigate]);

  useEffect(() => {
    if (!isAuthenticated || !isAdmin) return;

    const load = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const { categories: cats } = await howtoApi.listCategories();
        setCategories(cats);

        if (isNew) {
          setForm({
            ...emptyForm(),
            categoryId: cats[0]?.id || '',
          });
          return;
        }

        // Load by listing and finding id (API get is by slug for users)
        const { guides } = await howtoApi.listGuides();
        const guide = guides.find(g => g.id === id);
        if (!guide) {
          setError('Guide not found');
          return;
        }
        setGuideId(guide.id);
        setForm({
          title: guide.title,
          slug: guide.slug,
          categoryId: guide.categoryId,
          status: guide.status,
          sortOrder: guide.sortOrder,
          contentHtml: guide.contentHtml,
          videoUrl: guide.videoUrl || '',
          galleryJson: guide.galleryJson || [],
          slugTouched: true,
        });
      } catch (err: any) {
        console.error('Failed to load how-to editor:', err);
        setError(err?.message || 'Failed to load guide');
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [isAuthenticated, isAdmin, isNew, id]);

  const updateField = useCallback(
    <K extends keyof HowToEditorForm>(key: K, value: HowToEditorForm[K]) => {
      setForm(prev => {
        const next = { ...prev, [key]: value };
        if (key === 'title' && !prev.slugTouched) {
          next.slug = generateSlugFromTitle(String(value));
        }
        if (key === 'slug') {
          next.slugTouched = true;
        }
        return next;
      });
    },
    []
  );

  const handleContentImageUpload = useCallback(async (file: File): Promise<string> => {
    const result = await uploadApi.uploadWithPresignedUrl(file, {
      componentType: 'howto',
      assetType: 'image',
    });
    return result.cloudfrontUrl || result.url;
  }, []);

  const addGalleryImage = useCallback((url: string, cloudfrontUrl?: string) => {
    setForm(prev => {
      if (prev.galleryJson.length >= 10) return prev;
      return {
        ...prev,
        galleryJson: [...prev.galleryJson, { url, cloudfrontUrl }],
      };
    });
  }, []);

  const removeGalleryImage = useCallback((index: number) => {
    setForm(prev => ({
      ...prev,
      galleryJson: prev.galleryJson.filter((_, i) => i !== index),
    }));
  }, []);

  const save = useCallback(
    async (status: HowToGuideStatus) => {
      if (!form.title.trim()) {
        setError('Title is required');
        return;
      }
      if (!form.categoryId) {
        setError('Category is required');
        return;
      }

      const payload = {
        categoryId: form.categoryId,
        title: form.title.trim(),
        slug: form.slug.trim() || generateSlugFromTitle(form.title),
        status,
        sortOrder: Number(form.sortOrder) || 0,
        contentHtml: form.contentHtml,
        videoUrl: form.videoUrl.trim() || null,
        galleryJson: form.galleryJson,
      };

      try {
        setIsSaving(true);
        setError(null);
        if (guideId) {
          await howtoApi.updateGuide(guideId, payload);
        } else {
          const { guide } = await howtoApi.createGuide(payload);
          setGuideId(guide.id);
          navigate(`/admin/howto/${guide.id}/edit`, { replace: true });
        }
        navigate('/admin/howto');
      } catch (err: any) {
        console.error('Failed to save guide:', err);
        setError(err?.message || 'Failed to save guide');
      } finally {
        setIsSaving(false);
      }
    },
    [form, guideId, navigate]
  );

  return {
    authLoading,
    isAuthenticated,
    isAdmin,
    logout,
    isNew,
    form,
    categories,
    isLoading,
    isSaving,
    error,
    updateField,
    handleContentImageUpload,
    addGalleryImage,
    removeGalleryImage,
    saveDraft: () => save('draft'),
    savePublish: () => save('published'),
    cancel: () => navigate('/admin/howto'),
  };
}
